/* eslint-disable react/prop-types */
/**
 * ApprovalsTab — approvals scoped to a deal.
 *
 * - Splits rows by `approval_type`: OFFER on top, PAY below (matches the
 *   R65 ordering in the inventory).
 * - Renders the `approval_status` column with a green/red AntD Tag so the
 *   user can scan APPROVED vs REJECTED at a glance.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Popconfirm, Space, Tag, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { numericCellRenderers } from "../../../utils/metadataV2Columns";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";
import {
  DEAL_STATUS_FIELD,
  dealStatusMetadataRow,
  withDealStatus,
  splitDealStatus,
} from "../../../utils/dealStatusField";
import {
  INTEREST_FIELD_RENDERERS,
  withShareBoundsNotice,
} from "../../../components/interestInput";

// Each approval type maps to TWO actions, because submitting a request and
// deciding it are different jobs held by different roles:
//   submitAction -> who may FILE the request (BD.BrokerAgent, BD.Landman)
//   decideAction -> who may approve/reject/remove it (BD.CapexApprover)
// This mirrors the de-hub router exactly: POST /approvals is
// `requires_roles([BROKER_AGENT, LANDMAN])`, while PUT/DELETE /approvals/{id}
// are `requires_roles([CAPEX_APPROVER])`.
const TYPE_DEFS = [
  {
    type: "OFFER",
    title: "Offer approvals",
    submitAction: "offer.submit",
    decideAction: "offer.approve",
  },
  {
    type: "PAY",
    title: "Pay approvals",
    submitAction: "pay.submit",
    decideAction: "pay.approve",
  },
];

// Strip from metadata before passing — the library panel has no `hideFields`
// prop, so pre-filtering is the supported way to suppress grid + form rows.
const HIDDEN_FIELDS = new Set(["deal_id", "approval_type"]);

// `approval_status` is a fixed app-layer pick list (data_type Lookup /
// input_type select, no `distinct_values_lookup`). The backend metadata
// carries no `options`, so the library's WigMetadataForm renders an empty
// select unless we supply them here. Values mirror the DB CHECK constraint
// `ck_approvals_approval_status` (de-hub migration 040): PENDING is the
// undecided default; APPROVED / REJECTED are the terminal decisions.
const APPROVAL_STATUS_OPTIONS = ["PENDING", "APPROVED", "REJECTED"];

// `approver` / `approved_at` are the decision's identity-of-record, and
// `submitted_by` / `submitted_date` are the request's. The caller must never
// type any of them: de-hub stamps the submission pair in create_approval
// (caller email + server clock, ignoring whatever the body carried, and
// immutable on PUT) and the decision pair when an approval is decided. We
// keep all four as read-only grid columns (so the team can see who filed or
// decided, and when) but strip them from the Add/Edit form via
// `formFieldFilter` — which is what makes the submitted-by / submitted-date
// values appear only after the request exists. The set mirrors the panel's
// own SERVER_MANAGED_FIELDS so overriding the filter doesn't re-expose audit
// columns.
const FORM_HIDDEN_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "approver",
  "approved_at",
  "submitted_by",
  "submitted_date",
]);
// `deal_status` is the one exception to `!row.hide`: it is a synthetic row
// carrying the DEAL's status into this dialog (feedback #26), hidden from the
// grid on purpose — a deal-level value repeated down every row of a child grid
// reads as a property of the row.
const formFieldFilter = (row) =>
  row.field === DEAL_STATUS_FIELD ||
  (!row.hide && !FORM_HIDDEN_FIELDS.has(row.field));

// A submitter FILES a request; they do not decide it. So the decision's own
// fields are off their form entirely:
//   approval_status — forced to PENDING in the create body below. Without
//     this, the Add form would offer a submitter the APPROVED option and
//     de-hub's create_approval would stamp `approver` with their own email —
//     a self-approval that never passes the CAPEX_APPROVER gate on PUT.
//   approval_notes  — the APPROVER's comments on the decision (metadata
//     migration 028, label "Approval Notes"). A submitter's own free text
//     belongs in `submission_notes` (migration 064, "Submission Notes"),
//     which stays on their form; letting a broker agent write the approver's
//     box lets them author words that read as the land team's rationale.
// `approver` / `approved_at` are already off both forms via FORM_HIDDEN_FIELDS.
const DECISION_ONLY_FIELDS = new Set(["approval_status", "approval_notes"]);
const submitterFormFieldFilter = (row) =>
  formFieldFilter(row) && !DECISION_ONLY_FIELDS.has(row.field);

// Color-code the APPROVED/REJECTED column. Uses library v1.0.94's
// `cellRenderers` prop (introduced for exactly this use case — see
// wig-react-library PR #142).
const statusTag = (value) => {
  if (!value) return null;
  const color = value === "APPROVED" ? "green" : value === "REJECTED" ? "red" : "default";
  return <Tag color={color}>{value}</Tag>;
};

/**
 * The `approval_status` cell.
 *
 * While a row is PENDING and the viewer may decide, it is two buttons —
 * the same pair, in the same order, with the same Popconfirm on Reject, as the
 * Approvals screen at the top of the app (feedback #29). Deciding used to mean
 * opening the edit form and changing a dropdown, which is a different
 * interaction for the same decision depending on which screen you were on.
 *
 * Anything already decided, or a viewer who may only submit, gets the Tag as
 * before — there is nothing to click.
 */
const statusRenderer = ({ mayDecide, decide, pendingId }) => {
  const ApprovalStatusCell = (params) => {
    const value = params?.value;
    const row = params?.data;
    if (!mayDecide || value !== "PENDING" || !row?.id) return statusTag(value);
    return (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      <Button
        size="small"
        type="primary"
        icon={<CheckOutlined />}
        loading={pendingId === row.id}
        onClick={() => decide(row, "APPROVED")}
      >
        Approve
      </Button>
      <Popconfirm
        title="Reject this approval?"
        okText="Reject"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        onConfirm={() => decide(row, "REJECTED")}
      >
        <Button
          size="small"
          danger
          icon={<CloseOutlined />}
          loading={pendingId === row.id}
        >
          Reject
        </Button>
        </Popconfirm>
      </Space>
    );
  };
  ApprovalStatusCell.displayName = "ApprovalStatusCell";
  return ApprovalStatusCell;
};

const ApprovalsTab = ({ dealId, dealKey, dealStatus, onDealChanged }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { can } = usePermissions();
  // `deal_status` is a field on the DEAL, not on the approval, so it carries
  // the deal's own field-level gate — the same one `DEAL_FIELD_ACTIONS` maps
  // it to and the server enforces per field. Offering it to a role the server
  // will reject turns one 403 into a failed save.
  const mayEditStatus = can("deal.editStatus");
  const offerStatus = Boolean(dealKey) && mayEditStatus;

  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, metaResp] = await Promise.all([
        dealsService.listApprovals({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "approvals" }),
      ]);
      setRows(listResp?.data?.data || []);
      setMetadata(metaResp?.data?.data || []);
    } catch (err) {
      console.error("ApprovalsTab load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredMetadata = useMemo(() => {
    const own = (metadata || [])
      .filter((m) => !HIDDEN_FIELDS.has(m.field))
      .map((m) =>
        m.field === "approval_status"
          ? { ...m, options: APPROVAL_STATUS_OPTIONS }
          : m
      );
    // Appended last so it sorts to the bottom of the form, below the approval's
    // own fields. Without a `dealKey` the PUT has no target, and a field that
    // silently does nothing is worse than one that is absent. `own.length`
    // keeps it out of the pre-load render, where a lone synthetic row would
    // look like loaded metadata to anything checking for one.
    if (!offerStatus || own.length === 0) return own;
    const lastOrd = own.reduce((n, m) => Math.max(n, m.ord ?? 0), 0);
    return [...own, dealStatusMetadataRow(lastOrd + 1)];
  }, [metadata, offerStatus]);

  // Merge the shared numeric formatters (keyed by field name — covers
  // delivered_nri at 8 dp, total_bonus whole-dollar currency, bonus_per_acre
  // 2-dp currency, and any other numeric "terms being approved") with the
  // approval_status Tag renderer. CELL_RENDERERS is spread last so the Tag
  // wins on any field-name collision. These fire only if the columns are
  // present, so this is safe to ship ahead of the backend metadata.
  /**
   * Record a decision straight from the grid.
   *
   * Status and type only: `deal_id` is immutable on `ApprovalUpdateRequest`
   * (extra="forbid"), so sending it 422s the PUT, and the server stamps
   * approver / approved_at itself on a terminal decision. Same body the
   * Approvals screen sends.
   */
  const decide = useCallback(
    async (row, decision) => {
      try {
        setPendingId(row.id);
        const { token: tk, userOid } = await getAuth();
        await dealsService.updateApproval({
          token: tk,
          userOid,
          id: row.id,
          body: {
            approval_status: decision,
            approval_type: row.approval_type,
          },
        });
        message.success(
          `${row.approval_type} approval ${
            decision === "APPROVED" ? "approved" : "rejected"
          }`
        );
        await refresh();
      } catch (err) {
        console.error("Approval decision failed:", err);
        message.error(apiErrorText(err, { fallback: "Failed to record decision" }));
      } finally {
        setPendingId(null);
      }
    },
    [getAuth, refresh]
  );

  const numericRenderers = useMemo(
    () => numericCellRenderers(filteredMetadata),
    [filteredMetadata]
  );

  // Built per type, not once: each approval type carries its own decide
  // permission, and the status cell is a pair of buttons only for someone who
  // holds it.
  const cellRenderersFor = useCallback(
    (mayDecide) => ({
      ...numericRenderers,
      approval_status: statusRenderer({ mayDecide, decide, pendingId }),
    }),
    [numericRenderers, decide, pendingId]
  );

  // Injects the full-width share-bounds banner at the top of the Terms section
  // (delivered_nri), composing with this tab's own formFieldFilter so the
  // approver/approved_at audit fields stay hidden.
  // Two variants: the decider's form keeps `approval_status` (that IS their
  // job); the submitter's drops it. Both are memoized off the same metadata.
  const deciderPanelProps = useMemo(
    () =>
      withShareBoundsNotice({
        metadata: filteredMetadata,
        customRenderers: INTEREST_FIELD_RENDERERS,
        formFieldFilter,
      }),
    [filteredMetadata]
  );

  const submitterPanelProps = useMemo(
    () =>
      withShareBoundsNotice({
        metadata: filteredMetadata,
        customRenderers: INTEREST_FIELD_RENDERERS,
        formFieldFilter: submitterFormFieldFilter,
      }),
    [filteredMetadata]
  );

  /**
   * Write the deal's status, if the dialog changed it.
   *
   * Called BEFORE the approval is written, and allowed to throw: a failure
   * here aborts the save, so an approval never exists implying a status change
   * that did not happen. See utils/dealStatusField.
   */
  const applyDealStatus = useCallback(
    async (status) => {
      if (status === undefined) return;
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateDeal({
        token: tk,
        userOid,
        dealKey,
        body: { deal_status: status },
      });
      if (onDealChanged) await onDealChanged();
    },
    [dealKey, getAuth, onDealChanged]
  );

  // `submitterOnly` re-applies the decision gate to the body itself, so a
  // submitter can't reach APPROVED or write the approver's notes even if
  // either field arrives in `body` some other way (a default, a stale draft,
  // a hand-crafted call). The form filter above is the same rule's UI half;
  // de-hub's role gates are the real boundary.
  const onCreateFor = (type, submitterOnly) => async (raw) => {
    // Split first: `deal_status` is not a column on `approvals` and the create
    // model is extra="forbid", so it must never reach the approval body.
    const { status, body } = splitDealStatus(raw, dealStatus);
    await applyDealStatus(status);
    const { token: tk, userOid } = await getAuth();
    const submitted = submitterOnly
      ? Object.fromEntries(
          Object.entries(body || {}).filter(
            ([field]) => !DECISION_ONLY_FIELDS.has(field)
          )
        )
      : body;
    const resp = await dealsService.createApproval({
      token: tk,
      userOid,
      body: {
        ...submitted,
        deal_id: dealId,
        approval_type: type,
        ...(submitterOnly ? { approval_status: "PENDING" } : {}),
      },
    });
    await refresh();
    return resp;
  };

  const onUpdate = (type) => async (id, raw) => {
    const { status, body } = splitDealStatus(raw, dealStatus);
    await applyDealStatus(status);
    const { token: tk, userOid } = await getAuth();
    // Strip server-managed/immutable fields (id, deal_id, created_*, updated_*);
    // approval_type stays (it's updatable and identifies the row's approval).
    const resp = await dealsService.updateApproval({
      token: tk,
      userOid,
      id,
      body: { ...stripReadOnlyForUpdate(body), approval_type: type },
    });
    await refresh();
    return resp;
  };


  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteApproval({ token: tk, userOid, id });
    await refresh();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }} className="wig-multi-table-tab">
      {errMsg && <Alert type="error" showIcon message="Failed to load approvals" description={errMsg} />}
      {TYPE_DEFS.map(({ type, title, submitAction, decideAction }) => {
        // Stamped with the deal's current status so the Edit dialog opens on
        // the real value rather than empty; `hide: true` keeps it off the grid.
        const typeRows = offerStatus
          ? withDealStatus(
              rows.filter((r) => r.approval_type === type),
              dealStatus
            )
          : rows.filter((r) => r.approval_type === type);
        // Two independent gates. Per WigMetadataCrudPanel's contract (see its
        // .d.ts), omitting onCreate hides the Add button, omitting onUpdate
        // suppresses the row-click Edit modal, and omitting onDelete hides the
        // delete column. So:
        //   submitter (BrokerAgent/Landman) -> Add only; rows are read-only
        //   decider  (CapexApprover)        -> Edit + Delete, no Add
        //   neither                         -> fully read-only
        // UI/UX only; the de-hub router is the real boundary, and this now
        // matches its split rather than contradicting it.
        const maySubmit = can(submitAction);
        const mayDecide = can(decideAction);
        const panelProps = mayDecide ? deciderPanelProps : submitterPanelProps;
        return (
          <Card key={type} size="small" title={`${title} (${typeRows.length})`}>
            <WigMetadataCrudPanel
              title={title}
              rows={typeRows}
              loading={loading}
              allowView
              disableNonEditable
              onCreate={maySubmit ? onCreateFor(type, !mayDecide) : undefined}
              onUpdate={mayDecide ? onUpdate(type) : undefined}
              onDelete={mayDecide ? onDelete : undefined}
              cellRenderers={cellRenderersFor(mayDecide)}
              // metadata / customRenderers / formFieldFilter: the delivered_nri
              // guard + the section banner. The submitter variant also drops
              // the decision's own fields (`approval_status`,
              // `approval_notes`) from the form.
              {...panelProps}
              newRowDefaults={{
                deal_id: dealId,
                approval_type: type,
                ...(mayDecide ? {} : { approval_status: "PENDING" }),
                // So Add opens on the deal's current status too, not blank —
                // blank would read as "no status" and save a clear.
                ...(offerStatus ? { [DEAL_STATUS_FIELD]: dealStatus } : {}),
              }}
              emptyText={`No ${title.toLowerCase()} recorded yet`}
              editTrigger="button"
              height={260}
            />
          </Card>
        );
      })}
    </Space>
  );
};

export default ApprovalsTab;
